import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box, Card, CardContent, Typography, Stack, Chip, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, CircularProgress, LinearProgress,
  Tab, Tabs, ToggleButton, ToggleButtonGroup, Tooltip, Alert,
  Dialog, DialogTitle, DialogContent, DialogActions, Button,
} from '@mui/material';
import {
  portfolioService, hhiLabel,
  type PortfolioResponse, type AllocationSlice,
} from '../services/portfolio-service';
import {
  AllocationTargetsService,
  type AllocationTarget,
} from '../services/allocation-targets-service';
import { TargetsTab } from '../components/allocation/TargetsTab';
import { DriftTab } from '../components/allocation/DriftTab';
import { RebalanceTab } from '../components/allocation/RebalanceTab';
import { ClassificationDialog } from '../components/allocation/ClassificationDialog';
import { httpClient } from '../services/http-client';
import { seriesColors } from '../styles/glass';
import { useColorMode } from '../theme';
import { baseMoney, useBaseCurrency } from '../stores/currency-store';

// A cross-account total, so it is reported in the household base currency.
// Components on this page call useBaseCurrency() so a change re-renders them.
const money = (n: string | number, digits = 0) => baseMoney(n, digits);

type Dim = 'by_symbol' | 'by_account' | 'by_owner';
type PageTab = 'overview' | 'targets' | 'drift' | 'rebalance';

/**
 * Portfolio allocation and composition.
 *
 * The Overview tab is strictly descriptive: it reports what the portfolio
 * holds, what it cost, and how concentrated it is. The Targets, Drift and
 * Rebalance tabs compare that against the household's OWN written target —
 * still arithmetic on their stated intent, never trade recommendations.
 */
export const AllocationPage: React.FC = () => {
  // Subscribes this page to the base currency so money() relabels on change.
  useBaseCurrency();
  const [data, setData] = useState<PortfolioResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [dim, setDim] = useState<Dim>('by_symbol');
  const [drill, setDrill] = useState<string | null>(null);
  const [timeline, setTimeline] = useState<any | null>(null);
  const [timelineLoading, setTimelineLoading] = useState(false);
  const [tab, setTab] = useState<PageTab>('overview');
  const [targets, setTargets] = useState<AllocationTarget[]>([]);
  const [classifyOpen, setClassifyOpen] = useState(false);
  // Bumped after classifications save so the drift tab refetches.
  const [driftEpoch, setDriftEpoch] = useState(0);
  const { mode } = useColorMode();

  useEffect(() => {
    portfolioService.get()
      .then(setData)
      .catch((e) => setError(e?.message ?? 'Failed to load portfolio'));
  }, []);

  const loadTargets = useCallback(() => {
    AllocationTargetsService.listTargets()
      .then(setTargets)
      .catch(() => setTargets([]));
  }, []);
  useEffect(() => { loadTargets(); }, [loadTargets]);

  // Distinct held symbols feed the classification editor.
  const heldSymbols = useMemo(
    () => Array.from(new Set((data?.holdings ?? []).map((h) => h.symbol))).sort(),
    [data],
  );

  const palette = useMemo(() => seriesColors(mode), [mode]);

  // The full dated history of the selected security, options included.
  useEffect(() => {
    if (!drill) { setTimeline(null); return; }
    let cancelled = false;
    setTimelineLoading(true);
    httpClient
      .get<any>(`/investments/symbol/${encodeURIComponent(drill)}/timeline`)
      .then((r) => { if (!cancelled) setTimeline((r as any)?.data ?? r); })
      .catch(() => { if (!cancelled) setTimeline(null); })
      .finally(() => { if (!cancelled) setTimelineLoading(false); });
    return () => { cancelled = true; };
  }, [drill]);
  const slices: AllocationSlice[] = data?.[dim] ?? [];

  if (error) return <Typography color="error" sx={{ p: 3 }}>{error}</Typography>;
  if (!data) {
    return (
      <Box sx={{ display: 'grid', placeItems: 'center', minHeight: 360, gap: 2 }}>
        <CircularProgress />
        <Typography variant="body2" color="text.secondary">Valuing your portfolio…</Typography>
      </Box>
    );
  }

  const t = data.totals;
  const c = data.concentration;
  const gainPositive = Number(t.gain_loss) >= 0;
  const unpriced = t.holding_count - t.priced_count;

  // Cumulative offsets drive the single stacked allocation bar.
  let acc = 0;
  const stacked = slices.map((s, i) => {
    const start = acc;
    acc += Number(s.percent);
    return { ...s, start, color: palette[i % palette.length] };
  });

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 2 }}>
        <Box>
          <Typography variant="overline" color="text.secondary">Investments</Typography>
          <Typography variant="h3">Allocation</Typography>
          <Typography variant="body2" color="text.secondary">
            {t.holding_count} holdings · {c.position_count} distinct symbols
          </Typography>
        </Box>
        <Stack direction="row" spacing={3} sx={{ textAlign: 'right' }}>
          <Metric label="Market value" value={money(t.market_value)} varName="--text" />
          <Metric label="Book cost" value={money(t.book_cost)} varName="--text-dim" />
          <Metric
            label="Unrealised"
            value={`${gainPositive ? '+' : ''}${money(t.gain_loss)} (${t.gain_pct}%)`}
            varName={gainPositive ? '--inflow-text' : '--outflow-text'}
          />
        </Stack>
      </Stack>

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
        <Tab label="Overview" value="overview" />
        <Tab label="Targets" value="targets" />
        <Tab label="Drift" value="drift" />
        <Tab label="Rebalance" value="rebalance" />
      </Tabs>

      {tab === 'targets' && (
        <TargetsTab
          targets={targets}
          onChanged={loadTargets}
          onOpenClassifications={() => setClassifyOpen(true)}
        />
      )}
      {/* key: classifications changed server-side, so remount to refetch. */}
      {tab === 'drift' && <DriftTab key={driftEpoch} targets={targets} />}
      {tab === 'rebalance' && <RebalanceTab key={driftEpoch} targets={targets} />}

      <ClassificationDialog
        open={classifyOpen}
        onClose={() => setClassifyOpen(false)}
        symbols={heldSymbols}
        onSaved={() => setDriftEpoch((e) => e + 1)}
      />

      {tab === 'overview' && (
      <>
      {unpriced > 0 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {unpriced} holding{unpriced === 1 ? '' : 's'} had no live quote and{' '}
          {unpriced === 1 ? 'is' : 'are'} shown at book cost
          {c.symbols_missing_price.length > 0 && `: ${c.symbols_missing_price.join(', ')}`}.
        </Alert>
      )}

      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ mb: 2 }}>
        <Card sx={{ flex: 2 }}>
          <CardContent>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
              <Typography variant="overline" color="text.secondary">Allocation</Typography>
              <ToggleButtonGroup
                size="small" exclusive value={dim}
                onChange={(_, v) => v && setDim(v)}
              >
                <ToggleButton value="by_symbol">Symbol</ToggleButton>
                <ToggleButton value="by_account">Account</ToggleButton>
                <ToggleButton value="by_owner">Owner</ToggleButton>
              </ToggleButtonGroup>
            </Stack>

            <Box sx={{
              display: 'flex', height: 14, borderRadius: 'var(--radius-pill)',
              overflow: 'hidden', mb: 2, border: '1px solid var(--border-soft)',
            }}>
              {stacked.map((s) => (
                <Tooltip key={s.key} title={`${s.key} · ${s.percent}% · ${money(s.value)}`}>
                  <Box sx={{ width: `${s.percent}%`, background: s.color, minWidth: 2 }} />
                </Tooltip>
              ))}
            </Box>

            <Stack spacing={0.9}>
              {stacked.map((s) => (
                <Box
                  key={s.key}
                  // Only a Symbol row identifies a security. Grouping by
                  // account or owner makes the key a container, and a
                  // per-symbol breakdown of it would be nonsense.
                  onClick={() => dim === 'by_symbol' && s.key && setDrill(s.key)}
                  tabIndex={dim === 'by_symbol' ? 0 : -1}
                  onKeyDown={(e) => {
                    if (dim === 'by_symbol' && s.key && (e.key === 'Enter' || e.key === ' ')) setDrill(s.key);
                  }}
                  sx={{
                    cursor: dim === 'by_symbol' ? 'pointer' : 'default',
                    borderRadius: 1,
                    '&:hover': dim === 'by_symbol' ? { bgcolor: 'action.hover' } : undefined,
                  }}
                >
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Box sx={{ width: 9, height: 9, borderRadius: '50%', background: s.color }} />
                      <Typography variant="body2">{s.key || '—'}</Typography>
                    </Stack>
                    <Typography variant="body2" className="fw-num" color="text.secondary">
                      {money(s.value)} · {s.percent}%
                    </Typography>
                  </Stack>
                  <LinearProgress
                    variant="determinate" value={Number(s.percent)}
                    sx={{ mt: 0.4, '& .MuiLinearProgress-bar': { background: s.color } }}
                  />
                </Box>
              ))}
            </Stack>
          </CardContent>
        </Card>

        <Card sx={{ flex: 1 }}>
          <CardContent>
            <Typography variant="overline" color="text.secondary">Concentration</Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
              How tightly the portfolio clusters. Descriptive only — not advice.
            </Typography>

            <Stack spacing={1.75}>
              <Box>
                <Typography variant="overline" color="text.secondary">Largest position</Typography>
                <Typography className="fw-num" variant="h5">
                  {c.top_symbol} · {c.top_weight_pct}%
                </Typography>
              </Box>
              <Box>
                <Typography variant="overline" color="text.secondary">Top 5 combined</Typography>
                <Typography className="fw-num" variant="h5">{c.top5_weight_pct}%</Typography>
              </Box>
              <Box>
                <Typography variant="overline" color="text.secondary">HHI</Typography>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Typography className="fw-num" variant="h5">{c.hhi}</Typography>
                  <Chip size="small" label={hhiLabel(Number(c.hhi))} />
                </Stack>
                <Typography variant="caption" color="text.secondary">
                  Herfindahl index over position weights (0–10,000)
                </Typography>
              </Box>
            </Stack>
          </CardContent>
        </Card>
      </Stack>

      <Card>
        <CardContent sx={{ pb: 1 }}>
          <Typography variant="overline" color="text.secondary">Holdings</Typography>
          <TableContainer sx={{ maxHeight: '55vh', mt: 1 }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Symbol</TableCell>
                  <TableCell>Account</TableCell>
                  <TableCell align="right">Shares</TableCell>
                  <TableCell align="right">ACB / share</TableCell>
                  <TableCell align="right">Book cost</TableCell>
                  <TableCell align="right">Price</TableCell>
                  <TableCell align="right">Market value</TableCell>
                  <TableCell align="right">Unrealised</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data.holdings.map((h) => {
                  const up = Number(h.gain_loss) >= 0;
                  return (
                    <TableRow key={`${h.account_id}-${h.symbol}`} hover>
                      <TableCell>
                        <Typography variant="body2" fontWeight={650}>{h.symbol}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {h.lot_count} lot{h.lot_count === 1 ? '' : 's'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption" color="text.secondary">{h.account_name}</Typography>
                      </TableCell>
                      <TableCell align="right" className="fw-num">{Number(h.shares).toFixed(2)}</TableCell>
                      <TableCell align="right" className="fw-num">{money(h.acb_per_share, 2)}</TableCell>
                      <TableCell align="right" className="fw-num">{money(h.book_cost, 2)}</TableCell>
                      <TableCell align="right" className="fw-num">
                        {h.has_price ? money(h.price, 2) : <Chip size="small" label="no quote" />}
                      </TableCell>
                      <TableCell align="right" className="fw-num">
                        {h.has_price ? money(h.market_value, 2) : '—'}
                      </TableCell>
                      <TableCell align="right" className="fw-num" sx={{
                        color: h.has_price
                          ? (up ? 'var(--inflow-text)' : 'var(--outflow-text)')
                          : 'var(--text-faint)',
                      }}>
                        {h.has_price ? `${up ? '+' : ''}${money(h.gain_loss, 2)} (${h.gain_pct}%)` : '—'}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>
      </>
      )}

      {/* Where one security actually sits: every account holding it, who owns
          that account, and the ACB per share. The pooled line at the bottom is
          the figure CRA cares about — identical property is pooled per
          taxpayer across accounts, so a per-account ACB is not what you report
          on a disposition. */}
      <Dialog open={!!drill} onClose={() => setDrill(null)} maxWidth="md" fullWidth>
        <DialogTitle>{drill}</DialogTitle>
        <DialogContent dividers>
          {(() => {
            const rows = (data?.holdings ?? []).filter((h) => h.symbol === drill);
            if (rows.length === 0) return <Typography>No positions.</Typography>;
            const shares = rows.reduce((a, h) => a + Number(h.shares || 0), 0);
            const cost = rows.reduce((a, h) => a + Number(h.book_cost || 0), 0);
            const owners = Array.from(new Set(rows.map((h) => h.owner_label || '—')));
            return (
              <>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Account</TableCell>
                      <TableCell>Owner</TableCell>
                      <TableCell align="right">Shares</TableCell>
                      <TableCell align="right">Book cost</TableCell>
                      <TableCell align="right">ACB / share</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {rows.map((h) => (
                      <TableRow key={h.account_id} hover>
                        <TableCell>{h.account_name}</TableCell>
                        <TableCell>{h.owner_label || '—'}</TableCell>
                        <TableCell align="right">{h.shares}</TableCell>
                        <TableCell align="right">{money(Number(h.book_cost))}</TableCell>
                        <TableCell align="right">{money(Number(h.acb_per_share))}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                {/* ── How the position was actually built ──────────────── */}
                <Box sx={{ mt: 3 }}>
                  <Typography variant="overline" color="text.secondary">
                    Timeline
                  </Typography>
                  {timelineLoading ? (
                    <Box sx={{ py: 2 }}><CircularProgress size={20} /></Box>
                  ) : !timeline || (timeline.events ?? []).length === 0 ? (
                    <Alert severity="info" sx={{ mt: 1 }}>
                      No trade history imported for this security. Positions
                      reported by an institution show cost basis but not the
                      purchases behind it.
                    </Alert>
                  ) : (
                    <>
                      <Stack direction="row" spacing={1} sx={{ my: 1.5, flexWrap: 'wrap', gap: 1 }}>
                        <Chip size="small" label={`Held ${timeline.shares_held}`} />
                        {Number(timeline.sale_proceeds) > 0 && (
                          <Chip size="small" variant="outlined"
                            label={`Sold ${money(Number(timeline.sale_proceeds))}`} />
                        )}
                        {Number(timeline.option_premium) !== 0 && (
                          <Chip size="small" variant="outlined" color="secondary"
                            label={`Option premium ${money(Number(timeline.option_premium))}`} />
                        )}
                        {Number(timeline.income_received) !== 0 && (
                          <Chip size="small" variant="outlined"
                            label={`Income ${money(Number(timeline.income_received))}`} />
                        )}
                        {Number(timeline.total_fees) > 0 && (
                          <Chip size="small" variant="outlined"
                            label={`Fees ${money(Number(timeline.total_fees))}`} />
                        )}
                      </Stack>

                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Date</TableCell>
                            <TableCell>Event</TableCell>
                            <TableCell align="right">Qty</TableCell>
                            <TableCell align="right">Price</TableCell>
                            <TableCell align="right">Position</TableCell>
                            <TableCell>Account</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {(timeline.events ?? []).map((e: any, i: number) => {
                            const isOption = !!e.contract;
                            const isSell = e.kind === 'SELL' || e.kind === 'OPTION_SELL';
                            return (
                              <TableRow key={`${e.date}-${i}`} hover>
                                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                  {new Date(e.date).toLocaleDateString()}
                                </TableCell>
                                <TableCell>
                                  <Chip
                                    size="small"
                                    sx={{ height: 20 }}
                                    color={isOption ? 'secondary' : isSell ? 'warning' : 'success'}
                                    variant={isOption ? 'filled' : 'outlined'}
                                    label={e.kind.replace('OPTION_', 'Option ').toLowerCase()}
                                  />
                                  {/* The contract an option row refers to. A
                                      covered call is an obligation against the
                                      shares above it, not a separate holding. */}
                                  {isOption && (
                                    <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                                      {e.contract}
                                    </Typography>
                                  )}
                                </TableCell>
                                <TableCell align="right">{e.shares}</TableCell>
                                <TableCell align="right">
                                  {Number(e.price) > 0 ? money(Number(e.price)) : '—'}
                                </TableCell>
                                <TableCell align="right">
                                  {/* Option trades do not move the share
                                      count, so the position is carried
                                      forward unchanged on those rows. */}
                                  {e.running_shares}
                                </TableCell>
                                <TableCell>
                                  <Typography variant="caption" color="text.secondary">
                                    {e.account}
                                  </Typography>
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>

                      {Number(timeline.opening_shares) !== 0 &&
                       !(timeline.events ?? []).some((e: any) => e.kind === 'BUY') && (
                        <Typography variant="caption" color="text.secondary"
                          sx={{ display: 'block', mt: 1 }}>
                          Opened at {timeline.opening_shares} shares. The purchases are
                          older than the transaction history the broker supplied, so the
                          line starts from what is held rather than from zero.
                        </Typography>
                      )}
                    </>
                  )}
                </Box>

                <Box sx={{ mt: 3 }}>
                  <Typography variant="overline" color="text.secondary">
                    Pooled across accounts
                  </Typography>
                  <Typography variant="body2">
                    {shares.toLocaleString()} shares · {money(cost)} book cost ·{' '}
                    {money(shares > 0 ? cost / shares : 0)} per share
                  </Typography>
                  {owners.length > 1 && (
                    <Typography variant="caption" color="warning.main" sx={{ display: 'block', mt: 1 }}>
                      Held by {owners.join(' and ')} — separate taxpayers, so these are
                      separate ACB pools and must not be combined.
                    </Typography>
                  )}
                </Box>
              </>
            );
          })()}
        </DialogContent>
        <DialogActions><Button onClick={() => setDrill(null)}>Close</Button></DialogActions>
      </Dialog>
    </Box>
  );
};

const Metric: React.FC<{ label: string; value: string; varName: string }> = ({ label, value, varName }) => (
  <Box>
    <Typography variant="overline" color="text.secondary">{label}</Typography>
    <Typography className="fw-num" variant="h5" sx={{ color: `var(${varName})` }}>{value}</Typography>
  </Box>
);
